import { useField } from 'formik';
import { getFormFieldClasses } from '@/utility/utility';
import { FieldWrapper, type FieldWrapperConfig } from '@/form/FieldWrapper';
import {
  ComboBox,
  type ComboBoxGroup,
  type ComboBoxOption,
  type ComboBoxOptionState,
  type MultiSelectOnChangeProps,
} from '@/components/ComboBox';

interface FormDropdownProps extends FieldWrapperConfig {
  options: ComboBoxOption[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  noEntriesMessage?: string;
  selectAllLabel?: string;
  className?: string;
  width?: string;
  none?: boolean;
  displaySearchBar?: boolean;
  popoverClassName?: string;
  onAddNew?: () => void;
  /** See `ComboBox` `searchMode`. */
  searchMode?: 'fuzzy' | 'words';
  noneLabel?: string;
  /** See `ComboBox` `groups`. */
  groups?: ComboBoxGroup[];
  /** See `ComboBox` `renderOption`. */
  renderOption?: (
    option: ComboBoxOption,
    state: ComboBoxOptionState
  ) => React.ReactNode;
}

/**
 * A Formik-connected dropdown field, backed by {@link ComboBox}.
 *
 * Handles single- and multi-select, an optional "none" entry, searching and an
 * "add new" action while wiring value/error state through Formik and rendering
 * the shared label + error space via {@link FieldWrapper}.
 */
export function FormDropdown({
  name,
  label,
  width,
  infoText,
  labelHint,
  labelButton,
  fieldClassName,
  showErrorSpace,
  className = '',
  disabled = false,
  multiple = false,
  onChange,
  none = false,
  ...props
}: MultiSelectOnChangeProps & FormDropdownProps) {
  const [field, meta, helpers] = useField(name);
  const isMultiple = multiple === '' || multiple === true;

  const wrapperProps = {
    name,
    label,
    disabled,
    infoText,
    labelHint,
    labelButton,
    fieldClassName,
    showErrorSpace,
    errorMeta: meta,
  };

  const handleChange = (val: unknown) => {
    // ComboBox clears a single select to `undefined`; keep the legacy empty
    // string so Formik values / validation stay consistent with the old field.
    const normalized = !isMultiple && val === undefined ? '' : val;
    helpers.setValue(normalized);
    helpers.setTouched(true, false);
    onChange?.(normalized as never);
  };

  return (
    <FieldWrapper {...wrapperProps}>
      <ComboBox
        {...props}
        multiple={multiple as never}
        none={none}
        width={width}
        disabled={disabled}
        value={field.value as never}
        onChange={handleChange as never}
        className={getFormFieldClasses({ meta, disabled, className })}
        error={meta.touched && meta.error ? meta.error : undefined}
      />
    </FieldWrapper>
  );
}
